import type { ReactNode } from 'react';

export type DiffRow = {
  path: string;
  kind: 'added' | 'removed' | 'changed';
  before?: unknown;
  after?: unknown;
};

const MAX_LEAF_PREVIEW = 80;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function formatLeaf(v: unknown): string {
  if (v === null || v === undefined) return '∅';
  if (typeof v === 'string') return v.length > MAX_LEAF_PREVIEW ? v.slice(0, MAX_LEAF_PREVIEW) + '…' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    const s = JSON.stringify(v);
    return s.length > MAX_LEAF_PREVIEW ? s.slice(0, MAX_LEAF_PREVIEW) + '…' : s;
  } catch {
    return '[unserializable]';
  }
}

function leavesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

export function diffConfig(prev: unknown, next: unknown, basePath = ''): DiffRow[] {
  const rows: DiffRow[] = [];
  const a = prev as Record<string, unknown> | undefined;
  const b = next as Record<string, unknown> | undefined;

  if (!isPlainObject(a) && !isPlainObject(b)) {
    if (!leavesEqual(a, b)) {
      rows.push({ path: basePath || '(root)', kind: 'changed', before: a, after: b });
    }
    return rows;
  }

  const keys = new Set<string>([
    ...(isPlainObject(a) ? Object.keys(a) : []),
    ...(isPlainObject(b) ? Object.keys(b) : []),
  ]);

  for (const key of Array.from(keys).sort()) {
    const path = basePath ? `${basePath}.${key}` : key;
    const av = isPlainObject(a) ? a[key] : undefined;
    const bv = isPlainObject(b) ? b[key] : undefined;

    if (av === undefined && bv !== undefined) {
      rows.push({ path, kind: 'added', after: bv });
      continue;
    }
    if (av !== undefined && bv === undefined) {
      rows.push({ path, kind: 'removed', before: av });
      continue;
    }

    if (isPlainObject(av) && isPlainObject(bv)) {
      rows.push(...diffConfig(av, bv, path));
      continue;
    }

    if (!leavesEqual(av, bv)) {
      rows.push({ path, kind: 'changed', before: av, after: bv });
    }
  }

  return rows;
}

export function RateDiffPreview({ rows }: { rows: DiffRow[] }): ReactNode {
  if (rows.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">No changes detected.</p>
    );
  }

  return (
    <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
      <p className="text-xs font-medium text-foreground">
        {rows.length} change{rows.length === 1 ? '' : 's'}
      </p>
      <ul className="space-y-1 text-xs font-mono">
        {rows.map((row) => (
          <li key={row.path} className="grid grid-cols-[auto_1fr] gap-2 items-baseline">
            <span className="text-muted-foreground">{row.path}</span>
            {row.kind === 'added' ? (
              <span className="text-success">+ {formatLeaf(row.after)}</span>
            ) : row.kind === 'removed' ? (
              <span className="text-destructive">− {formatLeaf(row.before)}</span>
            ) : (
              <span>
                <span className="text-muted-foreground">{formatLeaf(row.before)}</span>
                <span className="text-muted-foreground"> → </span>
                <span className="text-foreground">{formatLeaf(row.after)}</span>
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
